import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { Clipboard } from '@angular/cdk/clipboard';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CopyLinkService } from '../../services/copy-link.service';

interface FileShareLink {
  token: string;
  isActive: boolean;
}

let shareLinkInstanceCount = 0;

@Component({
  selector: 'app-share-link',
  standalone: true,
  imports: [CommonModule, NgbPopover],
  templateUrl: './share-link.component.html',
  styleUrl: './share-link.component.scss'
})
export class ShareLinkComponent {
  file = input.required<any>();
  showRestrictions = input.required<boolean>();
  showText = input.required<boolean>();
  linkToggled = output<any>();

  #destroyRef = inject(DestroyRef);
  #document = inject(DOCUMENT);
  #toaster = inject(ToastrService);
  #clipboard = inject(Clipboard);
  #copyLinkService = inject(CopyLinkService);

  readonly uid = `share-link-${shareLinkInstanceCount++}`;

  #fetchedLink = signal<{ ownerId: string; link: FileShareLink } | null>(null);

  #requestSeq = 0;

  isGeneratingLink = signal(false);
  isTogglingRestriction = signal(false);

  currentLink = computed<FileShareLink | null>(() => {
    const file = this.file();
    const fetched = this.#fetchedLink();
    const ownedByThisFile = fetched && fetched.ownerId === file?.id ? fetched.link : null;
    return ownedByThisFile ?? file?.link ?? null;
  });

  generatedShareLink = computed<string | null>(() => {
    const link = this.currentLink();
    return link ? `${this.#document.location.origin}/share?token=${link.token}` : null;
  });

  ensureLinkGenerated(): void {
    const file = this.file();
    if (!file?.id) {
      return;
    }

    const seq = ++this.#requestSeq;
    this.#fetchedLink.set(null);
    this.isGeneratingLink.set(true);

    this.#copyLinkService
      .getTokenForLinkSharingService(file, false)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: link => {
          if (seq !== this.#requestSeq) {
            return;
          }
          this.#fetchedLink.set({ ownerId: file.id, link });
          this.isGeneratingLink.set(false);
        },
        error: () => {
          if (seq === this.#requestSeq) {
            this.isGeneratingLink.set(false);
          }
        }
      });
  }

  copyToClipboard(text: string): void {
    this.#clipboard.copy(text);
    this.#toaster.success('Copying to clipboard was successful!', 'Success!');
  }

  toggleLinkRestriction(): void {
    const file = this.file();
    const link = this.currentLink();
    if (!file?.id || !link || this.isTogglingRestriction()) {
      return;
    }

    // Supersede any in-flight fetch so its pre-toggle response cannot overwrite the new state. The
    // link is already on screen at this point, so nothing is still "generating".
    const seq = ++this.#requestSeq;
    this.isGeneratingLink.set(false);
    this.isTogglingRestriction.set(true);

    this.#copyLinkService
      .toggleLinkRestrictionService(file, false)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (isActive: boolean) => {
          this.isTogglingRestriction.set(false);
          if (seq !== this.#requestSeq) {
            return;
          }

          if (typeof isActive === 'boolean') {
            this.#fetchedLink.set({ ownerId: file.id, link: { ...link, isActive } });
          }
          this.linkToggled.emit(file);
        },
        error: () => this.isTogglingRestriction.set(false)
      });
  }
}
